import { getToken } from "./tokens";
import {
  getAdminPPRPricing,
  createPPRPricing,
  updatePPRPricing,
  deactivatePPRPricing,
  getAdminCreditPackages,
  createCreditPackage,
  updateCreditPackage,
  getUserAirplanes,
  createAirplane,
  saveAirplane,
  url,
} from "../misc/api";

const API_BASE = url;

class APIClient {
  constructor(baseURL = API_BASE) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    const token = getToken();
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: token }),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === "object") {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        window.location.href = "/logout";
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(
        `API Error [${options.method || "GET"}] ${endpoint}:`,
        error
      );
      throw error;
    }
  }

  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url);
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: data,
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: data,
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: "DELETE",
    });
  }
}

// Generic wrapper for consistent error handling and response format
const wrapApiCall = async (apiCall) => {
  try {
    const response = await apiCall();
    return {
      data: response,
      success: true,
    };
  } catch (error) {
    return {
      data: null,
      success: false,
      error: error.message,
    };
  }
};

// Generic wrapper for array responses
const wrapArrayApiCall = async (apiCall) => {
  try {
    const response = await apiCall();
    return {
      data: response.data || [],
      success: true,
    };
  } catch (error) {
    return {
      data: [],
      success: false,
      error: error.message,
    };
  }
};

const createGetById = (getAllFn, idField) => async (id) => {
  try {
    const response = await getAllFn(true);
    const item = response.data?.find((item) => item[idField] === id);
    return {
      data: item || null,
      success: !!item,
    };
  } catch (error) {
    return {
      data: null,
      success: false,
      error: error.message,
    };
  }
};

const createCRUDAPI = (config) => ({
  getAll: () => wrapArrayApiCall(config.getAll),
  getById: config.getById || createGetById(config.getAll, config.idField),
  create: (data) => wrapApiCall(() => config.create(data)),
  update: (id, data) => wrapApiCall(() => config.update(id, data)),
  delete: (id) => wrapApiCall(() => config.delete(id)),
});

export const pprPricing = createCRUDAPI({
  getAll: () => getAdminPPRPricing(true),
  idField: "pricing_id",
  create: createPPRPricing,
  update: updatePPRPricing,
  delete: deactivatePPRPricing,
});

export const creditPackages = createCRUDAPI({
  getAll: () => getAdminCreditPackages(true),
  idField: "package_id",
  create: createCreditPackage,
  update: (id, data) => updateCreditPackage(data),
  delete: (id) => updateCreditPackage(id, { is_active: false }),
});

export const airplanes = createCRUDAPI({
  getAll: async () => {
    const response = await getUserAirplanes();
    return {
      data: response.data.map((item) => ({ ...item, id: item.plane_id })),
    };
  },
  idField: "plane_id",
  create: (data) =>
    createAirplane({ ...data, status: data.status || "active" }),
  update: (_, data) => saveAirplane(data),
  delete: (id) => saveAirplane({ plane_id: id, status: "disabled" }),
});

const api = new APIClient();

const createResourceAPI = (resourceName) => ({
  getAll: (filters = {}) => api.get(`${resourceName}`, filters),
  getById: (id) => api.get(`${resourceName}/${id}`),
  create: (data) => api.post(`${resourceName}`, data),
  update: (id, data) => api.put(`${resourceName}/${id}`, data),
  delete: (id) => api.delete(`${resourceName}/${id}`),
});

export const users = createResourceAPI("users");
export const flights = createResourceAPI("flights");
export const stands = createResourceAPI("stands");
export const payments = createResourceAPI("payments");

export const auth = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (data) => api.post("/auth/register", data),
  verifyPhone: (phone) => api.post("/auth/verify-sms", { phone }),
  verifyCode: (phone, code) => api.post("/auth/verify-code", { phone, code }),
};

export const admin = {
  getStats: () => api.get("/admin/stats"),
  confirmFlight: (id, notes) => api.post(`/flights/${id}/confirm`, { notes }),
  declineFlight: (id, reason) => api.post(`/flights/${id}/decline`, { reason }),
};

export default api;
